import React, { useState, useCallback, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  getAllForecastData,
  FORECAST_SEARCH_IDS,
} from "@/services/api/forecast";
import { Loader2 } from "lucide-react";
import SkuSalesForecast from "@/components/charts/SkuSalesForecast";
import SpuSalesForecast from "@/components/charts/SpuSalesForecast";
import SpPlanChart from "@/components/charts/SpPlanChart";
import KolSkuPromotionChart from "@/components/charts/KolSkuPromotionChart";
import WeeklySalesChart from "@/components/charts/WeeklySalesChart";
import DailySalesChart from "@/components/charts/DailySalesChart";
import PriceDailySalesChart from "@/components/charts/PriceDailySalesChart";
import PlatformWeeklySalesChart from "@/components/charts/PlatformWeeklySalesChart";
import TwoYearSpecSales from "@/components/charts/TwoYearSpecSales";
import TwoYearSpuSpecSales from "@/components/charts/TwoYearSpuSpecSales";
import TwoYearSpuSales from "@/components/charts/TwoYearSpuSales";
import TwoYearCategory3Sales from "@/components/charts/TwoYearCategory3Sales";
import axios from "axios";
import debounce from "lodash/debounce";
import { cn } from "@/lib/utils";

interface SearchResult {
  strValue: string;
  strKey: string;
}

const InventoryForecast: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState<{ [key: number]: any }>({});
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<SearchResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);

  
  // 监听滚动位置
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsSticky(!e.isIntersecting);
      },
      { threshold: [0] }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // 远程搜索
  const searchProducts = useCallback(
    debounce(async (value: string) => {
      if (!value.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await axios.get(
          "https://gate.shjinjia.com.cn/api/SearchTool/EexcuteDataSource",
          {
            params: {
              id: 852,
              keyword_value: value,
            },
            headers: {
              token: "be8cb528e6c8466e8866eafe3a55ac8e",
            },
          }
        );

        if (response.data.res_content) {
          setSearchResults(response.data.res_content);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error("Search failed:", error);
        toast({
          title: "搜索失败",
          description: "请稍后重试",
          variant: "destructive",
        });
      }
    }, 300),
    [toast]
  );

  // 获取图表数据
  const fetchChartData = async (productCode: string) => {
    if (!productCode) {
      toast({
        title: "请选择产品",
        description: "请先选择要查询的产品",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const results = await getAllForecastData(productCode);

      const processedData = results.reduce((acc, result) => {
        if (result.data && result.data.res_content) {
          acc[result.searchId] = result.data.res_content.data_list;
        }
        return acc;
      }, {} as { [key: number]: any });

      setChartData(processedData);
    } catch (error) {
      console.error("Data fetch failed:", error);
      toast({
        title: "获取数据失败",
        description: error instanceof Error ? error.message : "请稍后重试",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 解析 URL 参数中的产品编码
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productCode = urlParams.get('productCode');

    if (productCode) {
      console.log('从URL参数中检测到产品编码:', productCode);

      // 通过搜索API获取产品信息，以便获取产品名称
      const fetchProductInfo = async () => {
        try {
          const response = await axios.get(
            "https://gate.shjinjia.com.cn/api/SearchTool/EexcuteDataSource",
            {
              params: {
                id: 852,
                keyword_value: productCode,
              },
              headers: {
                token: "be8cb528e6c8466e8866eafe3a55ac8e",
              },
            }
          );

          if (response.data.res_content && response.data.res_content.length > 0) {
            // 从搜索结果中找到匹配的产品
            const matchedProduct = response.data.res_content.find(
              (result: SearchResult) => result.strValue === productCode
            );

            if (matchedProduct) {
              // 使用找到的产品信息
              setSelectedProduct(matchedProduct);
              setSearchValue(productCode);

              toast({
                title: "自动加载产品数据",
                description: `已从链接加载产品: ${matchedProduct.strKey} (${productCode})`,
              });
            } else {
              // 如果没找到精确匹配，使用第一个结果或创建模拟对象
              const firstResult = response.data.res_content[0];
              const urlProduct: SearchResult = {
                strValue: productCode,
                strKey: firstResult.strKey || productCode
              };
              setSelectedProduct(urlProduct);
              setSearchValue(productCode);

              toast({
                title: "自动加载产品数据",
                description: `已从链接加载产品: ${urlProduct.strKey} (${productCode})`,
              });
            }
          } else {
            // 搜索无结果时，创建模拟对象
            const urlProduct: SearchResult = {
              strValue: productCode,
              strKey: productCode
            };
            setSelectedProduct(urlProduct);
            setSearchValue(productCode);

            toast({
              title: "自动加载产品数据",
              description: `已从链接加载产品编码: ${productCode}`,
            });
          }
        } catch (error) {
          console.error("获取产品信息失败:", error);
          // 出错时创建模拟对象
          const urlProduct: SearchResult = {
            strValue: productCode,
            strKey: productCode
          };
          setSelectedProduct(urlProduct);
          setSearchValue(productCode);

          toast({
            title: "自动加载产品数据",
            description: `已从链接加载产品编码: ${productCode}`,
          });
        }

        // 延迟获取图表数据
        setTimeout(() => {
          fetchChartData(productCode);
        }, 100);
      };

      fetchProductInfo();
    }
  }, [toast]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 观察点 */}
      <div ref={observerRef} className="absolute top-0 h-1 w-full" />

      {/* 页面标题和搜索框容器 */}
      <div 
        ref={headerRef}
        className={cn(
          "bg-gray-50 transition-all duration-300",
          isSticky ? "sticky top-0 z-50 shadow-lg" : ""
        )}
      >
        <div className="p-6">
          <div className="flex items-center gap-6">
            {/* 页面标题 */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">销量预测看板</h1>
              <p className="text-gray-600 mt-1">
                基于时间序列的销量预测分析，支持SKU/SPU维度查询
              </p>
            </div>

            {/* 搜索框 */}
            <div className="flex-1">
              <div className="flex gap-3 relative">
                <div className="relative flex-1">
                  <Input
                    value={
                      selectedProduct
                        ? `${selectedProduct.strKey} (${selectedProduct.strValue})`
                        : searchValue
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchValue(value);
                      setSelectedProduct(null);
                      searchProducts(value);
                    }}
                    onFocus={() => {
                      if (searchResults.length > 0) {
                        setShowDropdown(true);
                      }
                    }}
                    placeholder="请输入产品编码或名称搜索"
                    className="w-full pl-10 pr-4 py-2 text-base rounded-lg border-gray-200 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                  />
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />

                  {/* 下拉搜索结果 */}
                  {showDropdown && searchResults.length > 0 && (
                    <div
                      className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[300px] overflow-y-auto"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {searchResults.map((result) => (
                        <div
                          key={result.strValue}
                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                          onClick={() => {
                            setSelectedProduct(result);
                            setSearchValue("");
                            setShowDropdown(false);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {result.strKey}
                            </span>
                            <span className="text-sm text-gray-500">
                              {result.strValue}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() =>
                    selectedProduct && fetchChartData(selectedProduct.strValue)
                  }
                  disabled={isLoading || !selectedProduct}
                  className="bg-[#13c19f] hover:bg-[#10a98c] text-white min-w-[80px] h-[36px] rounded-lg transition-colors duration-200 text-sm"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-5 h-4 mr-2" />
                      查询
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 图表内容区域 */}
      <div className="px-6">
        {/* 销量预测分析标题 */}
        {/* <h2 className="text-xl font-semibold text-gray-800 mb-6">销量预测分析</h2> */}

        {/* 图表网格布局 */}
        <div className="space-y-6">
          {/* 第一层：SKU和SPU销量预测 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">SKU销量预测</h3>
              {chartData[FORECAST_SEARCH_IDS.SKU_SALES] ? (
                <SkuSalesForecast
                  data={chartData[FORECAST_SEARCH_IDS.SKU_SALES] || []}
                  loading={isLoading}
                />
              ) : (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                  <p>暂无数据</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">{chartData[FORECAST_SEARCH_IDS.SPU_SALES]?.[0]?.title || 'SPU销量预测'}</h3>
              {chartData[FORECAST_SEARCH_IDS.SPU_SALES] ? (
                <SpuSalesForecast
                  data={chartData[FORECAST_SEARCH_IDS.SPU_SALES] || []}
                  loading={isLoading}
                />
              ) : (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                  <p>暂无数据</p>
                </div>
              )}
            </div>
          </div>

          {/* 第二层：两年内SPU销量数量和分类3销量情况 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU] && chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU].length > 0 ? (
                <>
                  <h3 className="text-lg font-semibold mb-4">
                    {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU][0]?.title || '两年内SPU销量数量'}
                  </h3>
                  <TwoYearSpuSales
                    data={chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU] || []}
                    loading={isLoading}
                  />
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-4">两年内SPU销量数量</h3>
                  <div className="h-[500px] flex items-center justify-center text-gray-400">
                    <p>暂无数据</p>
                  </div>
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_CATEGORY3] && chartData[FORECAST_SEARCH_IDS.TWO_YEAR_CATEGORY3].length > 0 ? (
                <>
                  <h3 className="text-lg font-semibold mb-4">
                    {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_CATEGORY3][0]?.title || '两年内分类3销量情况'}
                  </h3>
                  <TwoYearCategory3Sales
                    data={chartData[FORECAST_SEARCH_IDS.TWO_YEAR_CATEGORY3] || []}
                    loading={isLoading}
                  />
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-4">两年内分类3销量情况</h3>
                  <div className="h-[500px] flex items-center justify-center text-gray-400">
                    <p>暂无数据</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 第三层：规格销量和SPU规格销量 河流图 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">
                {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES] && chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES].length > 0 ?
                    chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES][0]?.title || '两年内规格销量情况' :
                    '两年内规格销量情况'
                }
              </h3>
              {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES] && chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES].length > 0 ? (
                  <TwoYearSpecSales
                      data={chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPEC_SALES] || []}
                      loading={isLoading}
                  />
              ) : (
                  <div className="h-[500px] flex items-center justify-center text-gray-400">
                    <p>暂无数据</p>
                  </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU_SPEC_SALES] && chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU_SPEC_SALES].length > 0 ? (
                  <>
                    <h3 className="text-lg font-semibold mb-4">
                      {chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU_SPEC_SALES][0]?.title || '两年内SPU规格销量情况(月销大于1000)'}
                    </h3>
                    <TwoYearSpuSpecSales
                        data={chartData[FORECAST_SEARCH_IDS.TWO_YEAR_SPU_SPEC_SALES] || []}
                        loading={isLoading}
                    />
                  </>
              ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-4">两年内SPU规格销量情况(月销大于1000)</h3>
                    <div className="h-[500px] flex items-center justify-center text-gray-400">
                      <p>暂无数据</p>
                    </div>
                  </>
              )}
            </div>
          </div>

          {/* 第四层：SP计划表和SPU营销数据 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">SP计划表</h3>
              {chartData[FORECAST_SEARCH_IDS.SP_PLAN] ? (
                <SpPlanChart
                  data={chartData[FORECAST_SEARCH_IDS.SP_PLAN] || []}
                  loading={isLoading}
                />
              ) : (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                  <p>暂无数据</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">SPU营销数据</h3>
              {chartData[FORECAST_SEARCH_IDS.SPU_MARKETING] ? (
                <KolSkuPromotionChart
                  data={chartData[FORECAST_SEARCH_IDS.SPU_MARKETING] || []}
                  loading={isLoading}
                />
              ) : (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                  <p>暂无数据</p>
                </div>
              )}
            </div>
          </div>



          {/* 第五层：SKU推广金额 & 每周销量 */}
          <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
            <h3 className="text-lg font-semibold mb-4">SKU推广金额 & 每周销量</h3>
            {chartData[FORECAST_SEARCH_IDS.WEEKLY_SALES] ? (
              <WeeklySalesChart
                data={chartData[FORECAST_SEARCH_IDS.WEEKLY_SALES] || []}
                loading={isLoading}
              />
            ) : (
              <div className="h-[500px] flex items-center justify-center text-gray-400">
                <p>暂无数据</p>
              </div>
            )}
          </div>

           {/* 第六层：SKU推广金额 & 每日销量 */}
         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
           <h3 className="text-lg font-semibold mb-4">
             SKU推广金额 & 每日销量 (最近60天)
           </h3>
           {chartData[FORECAST_SEARCH_IDS.SKU_DAILY] ? (
             <DailySalesChart
               data={chartData[FORECAST_SEARCH_IDS.SKU_DAILY] || []}
               loading={isLoading}
             />
           ) : (
             <div className="h-[500px] flex items-center justify-center text-gray-400">
               <p>暂无数据</p>
             </div>
           )}
         </div>

         {/* 第七层：SKU价格 & 每日销量 */}
         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
           <h3 className="text-lg font-semibold mb-4">
             SKU价格 & 每日销量 (最近60天)
           </h3>
           {chartData[FORECAST_SEARCH_IDS.SKU_PRICE_DAILY] ? (
             <PriceDailySalesChart
               data={chartData[FORECAST_SEARCH_IDS.SKU_PRICE_DAILY] || []}
               loading={isLoading}
             />
           ) : (
             <div className="h-[500px] flex items-center justify-center text-gray-400">
               <p>暂无数据</p>
             </div>
           )}
         </div>

         {/* 第八层：每周平台销量分布 */}
         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
           <h3 className="text-lg font-semibold mb-4">每周平台销量分布</h3>
           {chartData[FORECAST_SEARCH_IDS.PLATFORM_WEEKLY] ? (
             <PlatformWeeklySalesChart
               data={chartData[FORECAST_SEARCH_IDS.PLATFORM_WEEKLY] || []}
               loading={isLoading}
             />
           ) : (
             <div className="h-[500px] flex items-center justify-center text-gray-400">
               <p>暂无数据</p>
             </div>
           )}
         </div>
        </div>
      </div>

      {/* 点击空白处关闭下拉框 */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default InventoryForecast;